import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { switchLanguage } from '../../../utils/switchLanguage';

const LanguageSelector = ({ currentLanguage, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸', nativeName: 'English' },
    { code: 'de', label: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
    { code: 'es', label: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
    { code: 'fr', label: 'French', flag: '🇫🇷', nativeName: 'Français' },
    { code: 'pt', label: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
    { code: 'it', label: 'Italian', flag: '🇮🇹', nativeName: 'Italiano' },
    { code: 'nl', label: 'Dutch', flag: '🇳🇱', nativeName: 'Nederlands' }
  ];

  const currentLang = languages?.find(lang => lang?.code === currentLanguage) || languages?.[0];

  const handleLanguageSelect = (langCode) => {
    const newPath = switchLanguage(location?.pathname, langCode);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('selectedLanguage', langCode);
      localStorage.setItem('preferredLanguage', langCode);
    } catch (e) {
      // Handle localStorage errors silently
    }
    
    // Call parent callback if provided
    if (onLanguageChange) {
      onLanguageChange(langCode);
    }
    
    // Navigate to new path
    navigate(newPath);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="language-select flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:border-gray-600 transition-all duration-200 ease-in-out cursor-pointer min-h-[44px]"
        aria-label="Select Language"
        aria-expanded={isOpen}
        style={{ fontSize: '16px' }}
      >
        <span className="text-xl">{currentLang?.flag}</span>
        <span className="text-sm font-medium text-foreground hidden sm:inline">
          {currentLang?.nativeName}
        </span>
        <Icon 
          name={isOpen ? "ChevronUp" : "ChevronDown"} 
          size={16} 
          color="var(--color-foreground)"
        />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-md shadow-elevation-4 z-50 overflow-hidden">
            {languages?.map((lang) => (
              <button
                key={lang?.code}
                onClick={() => handleLanguageSelect(lang?.code)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 text-left
                  transition-smooth hover:bg-muted cursor-pointer
                  ${currentLanguage === lang?.code ? 'bg-muted' : ''}
                `}
                style={{ minHeight: '44px' }}
              >
                <span className="text-xl">{lang?.flag}</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-popover-foreground">
                    {lang?.nativeName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {lang?.label}
                  </div>
                </div>
                {currentLanguage === lang?.code && (
                  <Icon name="Check" size={16} color="var(--color-primary)" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;